import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '@/lib/api';
import { useSession } from '@/hooks/useSession';
import { PcRecommendedCallout } from '@/components/PcRecommendedCallout';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { PaginationBar } from '@/components/ui/PaginationBar';
import {
  inputClass,
  labelClass,
  tableWrapClass,
  tableClass,
  theadClass,
  thClass,
  inlineSuccessClass,
  inlineErrorClass,
} from '@/components/ui/inputStyles';

type Row = {
  id: number;
  site_id: number;
  site_name: string;
  request_id: string;
  event_source: string;
  status: string;
  http_code: number;
  jinsuju_ok: number;
  draft_status: string | null;
  draft_detail: string | null;
  form_name: string | null;
  serial_number: string | null;
  entry_id: number | null;
  created_at: string;
};

type ListResp = {
  ok: boolean;
  items: Row[];
  total: number;
  page: number;
  per_page: number;
  site_id?: number;
};

type PurgeResp = { ok: boolean; deleted?: number; err?: string; detail?: string };

const perPage = 15;

const purgeLabels: Record<string, string> = {
  older_than_1y: '1 年前',
  older_than_6m: '6 个月前',
  older_than_1m: '1 个月前',
  all: '全部',
};

const statusLabels: Record<string, string> = {
  saved: '已入库',
  rejected: '已拒绝',
  duplicate_webhook: '重复推送',
  invalid_payload: '无效载荷',
  ingest_disabled: '入库已关闭',
  save_failed: '保存失败',
};

function sourceLabel(source: string): string {
  if (source === 'site_master') return '站长登记';
  if (source === 'admin_manual') return '后台登记';
  return '金数据';
}

function sourceBadgeClass(source: string): string {
  if (source === 'site_master') {
    return 'inline-flex rounded-md bg-violet-50 px-2 py-0.5 text-xs font-medium text-violet-800 ring-1 ring-inset ring-violet-200';
  }
  if (source === 'admin_manual') {
    return 'inline-flex rounded-md bg-sky-50 px-2 py-0.5 text-xs font-medium text-sky-800 ring-1 ring-inset ring-sky-200';
  }
  return 'inline-flex rounded-md bg-teal-50 px-2 py-0.5 text-xs font-medium text-teal-800 ring-1 ring-inset ring-teal-200';
}

function statusLabel(status: string): string {
  return statusLabels[status] ?? status;
}

function draftLabel(status: string | null): string {
  if (!status) return '—';
  const m: Record<string, string> = {
    pending: '待推送',
    ok: '已推送',
    skipped: '已跳过',
    failed: '失败',
    review: '待审核',
  };
  return m[status] ?? status;
}

export function LogsPage() {
  const { currentSiteId, currentSiteName } = useSession();
  const [data, setData] = useState<Row[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [purgeBusy, setPurgeBusy] = useState<string | null>(null);
  const [actionMsg, setActionMsg] = useState('');
  const [actionErr, setActionErr] = useState('');

  const [qDraft, setQDraft] = useState('');
  const [sourceDraft, setSourceDraft] = useState('');
  const [ingestDraft, setIngestDraft] = useState('');
  const [qApplied, setQApplied] = useState('');
  const [sourceApplied, setSourceApplied] = useState('');
  const [ingestApplied, setIngestApplied] = useState('');

  const load = useCallback(
    async (pageOverride?: number, opts?: { silent?: boolean }) => {
      const p = pageOverride ?? page;
      const silent = opts?.silent === true;
      if (!silent) {
        setLoading(true);
      }
      setLoadError(null);
      try {
        const r = await apiFetch<ListResp>('list_pipeline_logs', {
          method: 'GET',
          query: {
            page: p,
            per_page: perPage,
            ...(qApplied ? { q: qApplied } : {}),
            ...(sourceApplied ? { event_source: sourceApplied } : {}),
            ...(ingestApplied ? { ingest: ingestApplied } : {}),
          },
        });
        if (r.ok) {
          setPage(p);
          setData(Array.isArray(r.items) ? r.items : []);
          setTotal(typeof r.total === 'number' ? r.total : 0);
        } else {
          setLoadError('加载失败');
        }
      } catch (e) {
        if (e instanceof Error && e.message === 'SESSION_EXPIRED') {
          setLoadError(null);
          return;
        }
        setLoadError(e instanceof Error ? e.message : '错误');
        setData([]);
        setTotal(0);
      } finally {
        if (!silent) {
          setLoading(false);
        }
      }
    },
    [page, qApplied, sourceApplied, ingestApplied],
  );

  useEffect(() => {
    if (currentSiteId < 1) {
      setLoading(false);
      return;
    }
    void load();
  }, [load, currentSiteId]);

  function runSearch() {
    setQApplied(qDraft.trim());
    setSourceApplied(sourceDraft);
    setIngestApplied(ingestDraft);
    setPage(1);
  }

  function resetSearch() {
    setQDraft('');
    setSourceDraft('');
    setIngestDraft('');
    setQApplied('');
    setSourceApplied('');
    setIngestApplied('');
    setPage(1);
  }

  async function purge(mode: 'older_than_1y' | 'older_than_6m' | 'older_than_1m' | 'all') {
    const label = purgeLabels[mode] ?? mode;
    const tip =
      mode === 'all'
        ? '确定清空当前站点的全部流水线日志？不可恢复。'
        : `确定删除当前站点「${label}」的流水线日志？不可恢复。`;
    if (!window.confirm(tip)) return;
    setPurgeBusy(mode);
    setActionMsg('');
    setActionErr('');
    try {
      const r = await apiFetch<PurgeResp>('purge_pipeline_logs', {
        method: 'POST',
        body: JSON.stringify({ mode }),
      });
      if (r.ok) {
        setActionMsg(`已删除 ${typeof r.deleted === 'number' ? r.deleted : 0} 条记录`);
        await load(1, { silent: true });
      } else {
        setActionErr(r.detail || r.err || '操作失败');
      }
    } catch (e) {
      setActionErr(e instanceof Error ? e.message : '操作失败');
    } finally {
      setPurgeBusy(null);
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const siteHint =
    currentSiteName.trim() !== '' ? currentSiteName.trim() : currentSiteId > 0 ? `站点 #${currentSiteId}` : '';

  if (currentSiteId < 1) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="日志中心"
          description="记录金数据 Webhook、站长登记与后台登记的信息入库与草稿推送结果。"
        />
        <p className="text-sm text-amber-800">请先在上方「当前工作站点」中选择站点后再查看流水线日志。</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="日志中心"
        description={
          siteHint !== ''
            ? `当前工作站点「${siteHint}」：金数据推送、站长手机端登记与后台登记的信息入库与草稿结果。`
            : '金数据推送、站长手机端登记与后台登记的信息入库与草稿结果。'
        }
      />

      <PcRecommendedCallout>
        日志列表与批量清理操作建议在电脑浏览器中进行，便于对照与确认。
      </PcRecommendedCallout>

      <Card title="筛选">
        <div className="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-end">
          <label className={`${labelClass} min-w-[12rem] flex-[2]`}>
            <span>关键词</span>
            <input
              className={inputClass}
              value={qDraft}
              onChange={(e) => setQDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') runSearch();
              }}
              placeholder="表单名 / 编号 / 条目 ID / 请求 ID / 草稿说明"
              spellCheck={false}
            />
          </label>
          <label className={`${labelClass} min-w-[10rem]`}>
            <span>信息来源</span>
            <select className={inputClass} value={sourceDraft} onChange={(e) => setSourceDraft(e.target.value)}>
              <option value="">全部</option>
              <option value="jinsuju">金数据</option>
              <option value="site_master">站长登记</option>
              <option value="admin_manual">后台登记</option>
            </select>
          </label>
          <label className={`${labelClass} min-w-[10rem]`}>
            <span>入库结果</span>
            <select className={inputClass} value={ingestDraft} onChange={(e) => setIngestDraft(e.target.value)}>
              <option value="">全部</option>
              <option value="ok">已入库</option>
              <option value="fail">未入库</option>
            </select>
          </label>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="primary" onClick={runSearch} disabled={loading}>
              查询
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={resetSearch}
              disabled={loading || (!qDraft && !sourceDraft && !ingestDraft && !qApplied && !sourceApplied && !ingestApplied)}
            >
              重置
            </Button>
          </div>
        </div>
      </Card>

      {loading ? (
        <Spinner label="加载日志" />
      ) : loadError ? (
        <EmptyState icon={<i className="fa-solid fa-triangle-exclamation text-2xl" aria-hidden />}>{loadError}</EmptyState>
      ) : (
        <Card
          title="流水线日志"
          titleExtra={
            <div className="flex flex-wrap items-center justify-end gap-2">
              <Button
                size="sm"
                variant="secondary"
                disabled={purgeBusy !== null}
                onClick={() => void purge('older_than_1y')}
              >
                {purgeBusy === 'older_than_1y' ? '处理中…' : '删除1年前日志'}
              </Button>
              <Button
                size="sm"
                variant="secondary"
                disabled={purgeBusy !== null}
                onClick={() => void purge('older_than_6m')}
              >
                {purgeBusy === 'older_than_6m' ? '处理中…' : '删除6个月前日志'}
              </Button>
              <Button
                size="sm"
                variant="secondary"
                disabled={purgeBusy !== null}
                onClick={() => void purge('older_than_1m')}
              >
                {purgeBusy === 'older_than_1m' ? '处理中…' : '删除1个月前日志'}
              </Button>
              <Button size="sm" variant="danger" disabled={purgeBusy !== null} onClick={() => void purge('all')}>
                {purgeBusy === 'all' ? '处理中…' : '清空日志'}
              </Button>
            </div>
          }
        >
          {actionMsg ? <p className={inlineSuccessClass + ' mb-3'}>{actionMsg}</p> : null}
          {actionErr ? <p className={inlineErrorClass + ' mb-3'}>{actionErr}</p> : null}
          {data.length === 0 ? (
            <EmptyState>暂无符合条件的流水线日志。</EmptyState>
          ) : (
            <>
              <div className={tableWrapClass}>
                <table className={tableClass}>
                  <thead className={theadClass}>
                    <tr>
                      <th className={thClass}>时间</th>
                      <th className={thClass}>入库站点</th>
                      <th className={thClass}>信息来源</th>
                      <th className={thClass}>状态</th>
                      <th className={thClass}>入库</th>
                      <th className={thClass}>草稿</th>
                      <th className={thClass}>表单/编号</th>
                      <th className={thClass}>条目 ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((r) => (
                      <tr key={r.id} className="border-t border-slate-100 align-top text-sm text-slate-800">
                        <td className="whitespace-nowrap px-3 py-2 text-xs text-slate-500">{r.created_at}</td>
                        <td className="px-3 py-2 text-xs text-slate-700">
                          <div className="max-w-[8rem] truncate font-medium" title={r.site_name || undefined}>
                            {r.site_name || (r.site_id > 0 ? `站点 #${r.site_id}` : '—')}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <span className={sourceBadgeClass(r.event_source)}>{sourceLabel(r.event_source)}</span>
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-600">{statusLabel(r.status)}</td>
                        <td className="px-3 py-2 text-xs">
                          {r.jinsuju_ok ? (
                            <span className="font-medium text-emerald-700">已入库</span>
                          ) : (
                            <span className="text-slate-400">未入库</span>
                          )}
                        </td>
                        <td className="max-w-[200px] px-3 py-2 text-xs">
                          {draftLabel(r.draft_status)}
                          {r.draft_detail ? <span className="mt-1 block text-slate-500">{r.draft_detail}</span> : null}
                        </td>
                        <td className="max-w-[160px] px-3 py-2 text-xs">
                          {r.form_name || '—'}
                          {r.serial_number ? (
                            <span className="mt-0.5 block font-mono text-[11px] text-slate-500">{r.serial_number}</span>
                          ) : null}
                        </td>
                        <td className="px-3 py-2 font-mono text-xs">{r.entry_id ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <PaginationBar
                page={page}
                totalPages={totalPages}
                total={total}
                onPrev={() => setPage((p) => Math.max(1, p - 1))}
                onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
                summary={total > 0 ? `共 ${total} 条` : undefined}
                className="mt-4"
              />
            </>
          )}
        </Card>
      )}
    </div>
  );
}
